import { Cross, EyeFilled, EyeOff, MoreHorizontal, Trashbin } from '@creditornot/cb-icons';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { ConfirmModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { useItemActionTracking } from 'modules/menu/hooks/telemetryHooks';
import { deleteOffer, deleteOfferBinding, getSelectionAll, updateOffer } from 'modules/shared-menu/api';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { SelectionCategory } from 'modules/shared-menu/types';

import { Action, PopoverContent, StyledPopover, ToggleButton } from './styles';
import { buildToggleEnabledPayload } from './utils';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../constants';
import { Offer } from '../../types';

type Props = {
  category: SelectionCategory | null;
  offer: Offer;
  selectionId: string;
  onCopy?: (itemId: string) => void;
};

const OfferActions = ({ category, offer, selectionId, onCopy }: Props) => {
  const { updateSelectionData } = useSelectionData();
  const trackOfferActions = useItemActionTracking();
  const menuPermissions = useOfferingMenuPermissionsContext();
  const [show, setShow] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleClose = () => setShow(false);

  const { mutate: handleDeleteOffer, isLoading: deleting } = useMutation(() => deleteOffer(selectionId, offer.id), {
    onSuccess: async () => {
      const updatedSelectionContainer = await getSelectionAll(selectionId, true);
      updateSelectionData({ actionType: 'updateSelectionContainer', payload: updatedSelectionContainer });
      toast.success(<FormattedMessage id="menu-item.deleteItemSuccess" />);
      trackOfferActions.trackDeleteItem(offer.id);
    },
    onError: () => {
      toast.error(<FormattedMessage id="errors.deleteItemFailed" />, { duration: Infinity });
    },
  });

  const { mutate: handleToggleEnabled } = useMutation(
    () => {
      const payload = buildToggleEnabledPayload(offer);
      return updateOffer(selectionId, offer.id, payload);
    },
    {
      onSuccess: updatedOffer => {
        updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
        toast.success(<FormattedMessage id="toast.success" />);
        const trackToggle = offer.enabled.enabled
          ? trackOfferActions.trackEnableItem
          : trackOfferActions.trackDisableItem;
        trackToggle(offer.id);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutate: handleRemoveFromCategory } = useMutation(
    () => {
      if (!category) {
        return Promise.reject();
      }
      return deleteOfferBinding(selectionId, category.id, offer.id);
    },
    {
      onSuccess: updatedSelection => {
        updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
        toast.success(<FormattedMessage id="toast.success" />);
        trackOfferActions.trackRemoveItemFromCategory(offer.id, category?.id || '');
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  if (!menuPermissions.contentEdit) {
    return null;
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <StyledPopover
        content={
          <PopoverContent>
            <Action variant="black" onClick={() => onCopy?.(offer.id)}>
              <FormattedMessage id="menu-item.duplicate" />
            </Action>

            {offer.enabled.enabled ? (
              <Action icon={<EyeOff />} variant="red" onClick={() => handleToggleEnabled()}>
                <FormattedMessage id="menu-item.disable" />
              </Action>
            ) : (
              <Action icon={<EyeFilled />} variant="blue" onClick={() => handleToggleEnabled()}>
                <FormattedMessage id="menu-item.enable" />
              </Action>
            )}

            <Action variant="red" onClick={() => setIsDeleteModalOpen(true)} icon={<Trashbin />}>
              <FormattedMessage id="menu-item.deleteItem" />
            </Action>

            {category && category.id !== WOLT_SPECIALS_CATEGORY_ID && (
              <Action variant="red" onClick={() => handleRemoveFromCategory()} icon={<Cross />}>
                <FormattedMessage id="menu-item.removeCategory" />
              </Action>
            )}
          </PopoverContent>
        }
        onClickOutside={handleClose}
        onClickInside={handleClose}
        show={show}
      >
        <ToggleButton
          tabIndex={-1}
          onClick={e => {
            e.preventDefault();
            setShow(!show);
          }}
        >
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>
      {isDeleteModalOpen && (
        <ConfirmModal
          header={<FormattedMessage id="menu-item.deleteItem" />}
          loading={deleting}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => handleDeleteOffer()}
          open
        />
      )}
    </div>
  );
};

export default React.memo(OfferActions);
